import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'

import {KpisQueryKey, PlantEquipmentStatus} from '../../types'

const PATH = '/kpi/dashboard/plants/equipment-status'
const INTERVAL_PERIOD_IN_MS = 300000

type EquipmentStatusTimeRange = {
  startDate: string
  endDate: string
}

type Options = Omit<
  UseQueryOptions<PlantEquipmentStatus[], AxiosError, Record<string, PlantEquipmentStatus>>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: AxiosError) => void
}

export const useEquipmentStatus = (
  timeRange: EquipmentStatusTimeRange,
  hierarchyId?: string,
  options?: Options
) => {
  const {onError, ...queryOptions} = options ?? {}

  const query = useQuery<PlantEquipmentStatus[], AxiosError, Record<string, PlantEquipmentStatus>>({
    queryKey: [KpisQueryKey.EquipmentStatus, timeRange.startDate, timeRange.endDate, hierarchyId],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<PlantEquipmentStatus[]>(PATH, {
        params: {
          from: timeRange.startDate,
          to: timeRange.endDate,
          ...(hierarchyId && {hierarchyId})
        },
        signal
      })

      return response.data
    },
    refetchInterval: INTERVAL_PERIOD_IN_MS,
    refetchOnMount: false,
    select: (data) => Object.fromEntries(data.map((plant) => [plant.plantId, plant])),
    ...queryOptions
  })

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
